/**
 * Tiny line glyphs for the apparatus pickers — one per stand, per burner, and
 * per vessel shape. Drawn rather than emoji: there is no emoji for "retort
 * stand" or "spirit lamp", and the nearest stand-ins (⚖️ 🏗️) say the wrong
 * thing entirely. The vessel glyph is the real silhouette the bench draws,
 * scaled down, so a picker tile can never disagree with the bench.
 */
import type { StandType } from '../standTypes';
import type { BurnerType } from '../burnerTypes';
import { VESSEL_2D } from '../vessel2D';
import type { VesselType } from '../vesselTypes';

const STAND_PATHS: Record<StandType, React.JSX.Element> = {
  // Never shown in a picker (STAND_TYPES omits it), but the record has to be
  // total so a stand added later can't be forgotten here.
  none: (
    <>
      <path d="M8 7 L8 15 Q8 17 10 17 L14 17 Q16 17 16 15 L16 7" />
      <line x1="3" y1="20" x2="21" y2="20" />
    </>
  ),
  // Tripod legs under a wire gauze.
  tripod: (
    <>
      <line x1="5" y1="10" x2="19" y2="10" />
      <line x1="7" y1="10" x2="4" y2="19" />
      <line x1="17" y1="10" x2="20" y2="19" />
      <line x1="12" y1="10" x2="12" y2="19" />
      <line x1="3" y1="20" x2="21" y2="20" />
    </>
  ),
  // Weighted base, upright rod, one clamp arm.
  retort: (
    <>
      <line x1="13" y1="18" x2="21" y2="18" />
      <line x1="17" y1="18" x2="17" y2="5" />
      <line x1="17" y1="10" x2="8" y2="10" />
      <path d="M8 7 L8 13" />
      <line x1="3" y1="20" x2="21" y2="20" />
    </>
  ),
  // A rack of wells.
  'tube-rack': (
    <>
      <rect x="3" y="8" width="18" height="3" rx="1" />
      <rect x="3" y="16" width="18" height="3" rx="1" />
      <line x1="4" y1="9" x2="4" y2="18" />
      <line x1="20" y1="9" x2="20" y2="18" />
      <line x1="8" y1="9.5" x2="8" y2="9.5" strokeLinecap="round" strokeWidth="3" />
      <line x1="12" y1="9.5" x2="12" y2="9.5" strokeLinecap="round" strokeWidth="3" />
      <line x1="16" y1="9.5" x2="16" y2="9.5" strokeLinecap="round" strokeWidth="3" />
    </>
  ),
  // One rod, two clamps at different heights.
  'multi-level': (
    <>
      <line x1="13" y1="18" x2="21" y2="18" />
      <line x1="17" y1="18" x2="17" y2="3" />
      <line x1="17" y1="6" x2="8" y2="6" />
      <path d="M8 4 L8 8" />
      <line x1="17" y1="14" x2="8" y2="14" />
      <path d="M8 12 L8 16" />
      <line x1="3" y1="20" x2="21" y2="20" />
    </>
  ),
};

const BURNER_PATHS: Record<BurnerType, React.JSX.Element> = {
  // Nothing under the vessel — a struck-through flame.
  none: (
    <>
      <path d="M12 6 C15 10 16 13 12 17 C8 13 9 10 12 6 Z" />
      <line x1="5" y1="19" x2="19" y2="5" />
    </>
  ),
  // Tall barrel on a round base, flame on top.
  bunsen: (
    <>
      <path d="M12 2 C14.5 5.5 15.5 8 12 11 C8.5 8 9.5 5.5 12 2 Z" />
      <line x1="12" y1="11" x2="12" y2="19" />
      <line x1="8" y1="21" x2="16" y2="21" />
    </>
  ),
  // Squat reservoir with a wick.
  'spirit-lamp': (
    <>
      <path d="M12 3 C14 5.5 14.8 7.5 12 10 C9.2 7.5 10 5.5 12 3 Z" />
      <line x1="12" y1="10" x2="12" y2="13" />
      <path d="M6 21 C5 16 7.5 13 12 13 C16.5 13 19 16 18 21 Z" />
    </>
  ),
};

export function StandIcon({ stand, size = 24 }: { stand: StandType; size?: number }): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24" width={size} height={size} aria-hidden="true"
      fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"
    >
      {STAND_PATHS[stand]}
    </svg>
  );
}

export function BurnerIcon({ burner, size = 24 }: { burner: BurnerType; size?: number }): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24" width={size} height={size} aria-hidden="true"
      fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"
    >
      {BURNER_PATHS[burner]}
    </svg>
  );
}

/** The vessel's own silhouette, boxed to `size`. */
export function VesselGlyph({ vessel, size = 24 }: { vessel: VesselType; size?: number }): React.JSX.Element {
  const art = VESSEL_2D[vessel];
  const halfW = art.maxHalfWidthPx + 4;
  return (
    <svg
      viewBox={`${-halfW} ${-art.heightPx - 4} ${halfW * 2} ${art.heightPx + 8}`}
      width={size} height={size} aria-hidden="true"
      fill="none" stroke="currentColor" strokeWidth={7} strokeLinejoin="round" strokeLinecap="round"
      preserveAspectRatio="xMidYMid meet"
    >
      <path d={art.outlinePath} />
    </svg>
  );
}

/** The two tools, in the same line weight as the apparatus beside them. */
export function ToolIcon({ tool, size = 24 }: { tool: 'ph-paper' | 'glass-rod'; size?: number }): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24" width={size} height={size} aria-hidden="true"
      fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"
    >
      {tool === 'ph-paper' ? (
        // A strip, its dipped end coloured in.
        <>
          <rect x="9" y="3" width="6" height="18" rx="1" />
          <line x1="9" y1="15" x2="15" y2="15" />
          <line x1="9" y1="18" x2="15" y2="18" />
        </>
      ) : (
        // A rod, leaning, a drop at its tip.
        <>
          <line x1="17" y1="3" x2="8" y2="18" />
          <path d="M7 19.5 Q6 21 7 22 Q8 21 7 19.5" />
        </>
      )}
    </svg>
  );
}
